import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { useMe } from '../session.js';
import { CheckIcon } from './icons.js';
import { Card } from './ui.js';

type Step = 'site' | 'verify' | 'alertTest' | 'contentWatch';

const hideKey = (accountId: string) => `wg:firstSteps:hidden:${accountId}`;
const readHidden = (accountId: string) => {
  try {
    return window.localStorage.getItem(hideKey(accountId)) === '1';
  } catch {
    return false;
  }
};

/**
 * First steps of a new account (T-65): add a site, confirm ownership, send a test alert, watch a
 * phone number or text. Shown on the overview until done; it can be hidden on this device.
 */
export function FirstSteps() {
  const { t } = useTranslation();
  const accountId = useMe().data?.account.id ?? '';
  const [dismissed, setDismissed] = useState(false);
  // Read on every render: the account id arrives after the first one.
  const hidden = dismissed || (accountId !== '' && readHidden(accountId));
  const state = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => unwrap(api.GET('/onboarding')),
  });
  const data = state.data;
  if (!data || data.completed || hidden) return null;

  const site = data.siteId;
  const steps: { key: Step; done: boolean; to: string }[] = [
    { key: 'site', done: data.steps.site, to: '/sites/new' },
    {
      key: 'verify',
      done: data.steps.verify,
      to: site ? `/sites/${site}/settings` : '/sites/new',
    },
    { key: 'alertTest', done: data.steps.alertTest, to: '/notifications' },
    ...(data.contentWatchAvailable
      ? [
          {
            key: 'contentWatch' as const,
            done: data.steps.contentWatch,
            to: site ? `/sites/${site}/settings` : '/sites/new',
          },
        ]
      : []),
  ];
  const done = steps.filter((s) => s.done).length;
  const hide = () => {
    try {
      window.localStorage.setItem(hideKey(accountId), '1');
    } catch {
      // Private mode: hidden until the page is reloaded.
    }
    setDismissed(true);
  };

  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold">{t('firstSteps.title')}</h2>
          <p className="text-slate-700">
            {t('firstSteps.progress', { done, total: steps.length })}
          </p>
        </div>
        <button
          type="button"
          onClick={hide}
          className="min-h-11 rounded-md px-2 text-sm text-slate-700 underline hover:text-slate-900"
        >
          {t('firstSteps.hide')}
        </button>
      </div>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-label={t('firstSteps.title')}
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={done}
      >
        <div
          className="h-full rounded-full bg-green-700"
          style={{ width: `${Math.round((done / steps.length) * 100)}%` }}
        />
      </div>
      <ol className="mt-4 flex flex-col gap-2">
        {steps.map((s) => (
          <li
            key={s.key}
            className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 ${s.done ? 'border-green-200 bg-green-50' : 'border-slate-200'}`}
          >
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${s.done ? 'bg-green-700 text-white' : 'border-2 border-slate-300'}`}
              aria-hidden="true"
            >
              {s.done ? <CheckIcon /> : null}
            </span>
            <span className="min-w-0 flex-1">
              <strong className={s.done ? 'text-slate-700 line-through' : ''}>
                {t(`firstSteps.steps.${s.key}.title`)}
              </strong>
              <span className="sr-only">
                {' '}
                – {s.done ? t('firstSteps.done') : t('firstSteps.open')}
              </span>
              {!s.done ? (
                <span className="block text-sm text-slate-700">
                  {t(`firstSteps.steps.${s.key}.text`)}
                </span>
              ) : null}
            </span>
            {!s.done ? (
              <Link
                to={s.to}
                className="inline-flex min-h-11 items-center rounded-md border border-blue-700 px-3 font-semibold text-blue-800 hover:bg-blue-50"
              >
                {t(`firstSteps.steps.${s.key}.action`)}
              </Link>
            ) : null}
          </li>
        ))}
      </ol>
    </Card>
  );
}
